import type { Brush, GameMap, Vec3 } from '@game/shared';

/** Axis-aligned bounding box in min/max form — the runtime collision primitive. */
export interface Aabb {
  minX: number;
  minY: number;
  minZ: number;
  maxX: number;
  maxY: number;
  maxZ: number;
}

export const aabbFromBrush = (brush: Brush): Aabb => ({
  minX: brush.center.x - brush.half.x,
  minY: brush.center.y - brush.half.y,
  minZ: brush.center.z - brush.half.z,
  maxX: brush.center.x + brush.half.x,
  maxY: brush.center.y + brush.half.y,
  maxZ: brush.center.z + brush.half.z,
});

export const aabbFromCenterHalf = (
  cx: number,
  cy: number,
  cz: number,
  hx: number,
  hy: number,
  hz: number,
): Aabb => ({
  minX: cx - hx,
  minY: cy - hy,
  minZ: cz - hz,
  maxX: cx + hx,
  maxY: cy + hy,
  maxZ: cz + hz,
});

export const aabbOverlaps = (a: Aabb, b: Aabb): boolean =>
  a.minX < b.maxX &&
  a.maxX > b.minX &&
  a.minY < b.maxY &&
  a.maxY > b.minY &&
  a.minZ < b.maxZ &&
  a.maxZ > b.minZ;

export interface RayHit {
  /** Distance along the ray, in world units. */
  distance: number;
  normalX: number;
  normalY: number;
  normalZ: number;
}

/**
 * Slab test. `dirInv` is precomputed 1/dir per axis so a ray can be tested
 * against thousands of boxes without a divide each time.
 */
export const rayAabb = (
  ox: number,
  oy: number,
  oz: number,
  invDx: number,
  invDy: number,
  invDz: number,
  box: Aabb,
  maxDistance: number,
): RayHit | null => {
  const tx1 = (box.minX - ox) * invDx;
  const tx2 = (box.maxX - ox) * invDx;
  let tmin = Math.min(tx1, tx2);
  let tmax = Math.max(tx1, tx2);
  let axis = 0;
  let sign = tx1 > tx2 ? 1 : -1;

  const ty1 = (box.minY - oy) * invDy;
  const ty2 = (box.maxY - oy) * invDy;
  const tyMin = Math.min(ty1, ty2);
  if (tyMin > tmin) {
    tmin = tyMin;
    axis = 1;
    sign = ty1 > ty2 ? 1 : -1;
  }
  tmax = Math.min(tmax, Math.max(ty1, ty2));

  const tz1 = (box.minZ - oz) * invDz;
  const tz2 = (box.maxZ - oz) * invDz;
  const tzMin = Math.min(tz1, tz2);
  if (tzMin > tmin) {
    tmin = tzMin;
    axis = 2;
    sign = tz1 > tz2 ? 1 : -1;
  }
  tmax = Math.min(tmax, Math.max(tz1, tz2));

  if (tmax < Math.max(tmin, 0) || tmin > maxDistance) return null;

  const distance = tmin < 0 ? 0 : tmin;
  return {
    distance,
    normalX: axis === 0 ? sign : 0,
    normalY: axis === 1 ? sign : 0,
    normalZ: axis === 2 ? sign : 0,
  };
};

/**
 * Static world collision. A uniform grid over the XZ plane keeps broad-phase
 * queries cheap; brush-based maps are sparse enough that this beats a BVH for
 * both build time and cache behaviour.
 */
export class CollisionWorld {
  readonly boxes: Aabb[] = [];
  readonly killPlaneY: number;

  private readonly cellSize = 8;
  private readonly cells = new Map<number, number[]>();

  constructor(map: GameMap) {
    this.killPlaneY = map.killPlaneY;
    for (const brush of map.brushes) this.boxes.push(aabbFromBrush(brush));
    for (let i = 0; i < this.boxes.length; i++) this.insert(i);
  }

  private cellKey(cx: number, cz: number): number {
    // 16-bit signed components packed into one integer key.
    return ((cx & 0xffff) << 16) | (cz & 0xffff);
  }

  private insert(index: number): void {
    const box = this.boxes[index]!;
    const x0 = Math.floor(box.minX / this.cellSize);
    const x1 = Math.floor(box.maxX / this.cellSize);
    const z0 = Math.floor(box.minZ / this.cellSize);
    const z1 = Math.floor(box.maxZ / this.cellSize);
    for (let cx = x0; cx <= x1; cx++) {
      for (let cz = z0; cz <= z1; cz++) {
        const key = this.cellKey(cx, cz);
        const bucket = this.cells.get(key);
        if (bucket) bucket.push(index);
        else this.cells.set(key, [index]);
      }
    }
  }

  /** Appends indices of boxes whose cells overlap `query` into `out`. May contain duplicates. */
  queryBroadphase(query: Aabb, out: number[]): void {
    out.length = 0;
    const x0 = Math.floor(query.minX / this.cellSize);
    const x1 = Math.floor(query.maxX / this.cellSize);
    const z0 = Math.floor(query.minZ / this.cellSize);
    const z1 = Math.floor(query.maxZ / this.cellSize);
    for (let cx = x0; cx <= x1; cx++) {
      for (let cz = z0; cz <= z1; cz++) {
        const bucket = this.cells.get(this.cellKey(cx, cz));
        if (!bucket) continue;
        for (const index of bucket) if (!out.includes(index)) out.push(index);
      }
    }
  }

  /**
   * Nearest static hit along a ray. Brute force over all brushes: maps at this
   * scale have a few hundred, and hitscans happen a handful of times per tick.
   */
  raycast(origin: Vec3, direction: Vec3, maxDistance: number): RayHit | null {
    const invDx = 1 / (direction.x === 0 ? 1e-12 : direction.x);
    const invDy = 1 / (direction.y === 0 ? 1e-12 : direction.y);
    const invDz = 1 / (direction.z === 0 ? 1e-12 : direction.z);
    let best: RayHit | null = null;
    for (const box of this.boxes) {
      const hit = rayAabb(
        origin.x,
        origin.y,
        origin.z,
        invDx,
        invDy,
        invDz,
        box,
        maxDistance,
      );
      if (hit && (!best || hit.distance < best.distance)) best = hit;
    }
    return best;
  }
}
